/**
 * Goodbye Letter (RESPA Notice of Servicing Transfer) — type definitions.
 *
 * This is the canonical home for goodbye-letter types. Both the browser
 * (via `src/types/goodbye-letter.ts` re-export shim) and the
 * `send-goodbye-letter` edge function import from here.
 *
 * MUST stay dependency-free (no Supabase, no logger, no DOM, no other shared
 * modules) so it remains safely importable from both Vite-bundled browser
 * code and Deno edge code.
 */

// ---------------------------------------------------------------------------
// EncompassContactType — local copy of the union from
// src/types/investor-encompass.ts so this file has no src/* deps. Browser
// callers continue to import from @/types/investor-encompass via the
// re-export shim added there.
// ---------------------------------------------------------------------------

export type EncompassContactType =
  | 'NoteDelivery'
  | 'LoanDelivery'
  | 'ShipTo'
  | 'CustomerService'
  | 'TrailingDocs'
  | 'MortgageInsurance'
  | 'Payment'
  | 'Insurance'
  | 'Assignment'
  | 'TaxNotice'
  | 'Correspondence'
  | 'Secondary'
  | 'Generic1'
  | 'Generic2'
  | 'Generic3'
  | 'Generic4';

// ---------------------------------------------------------------------------
// DB row + camelCase view
// ---------------------------------------------------------------------------

export interface GoodbyeLetterSettingsRow {
  id: string;
  organization_id: string;
  template_id: string;

  payment_contact_role: EncompassContactType;
  customer_service_contact_role: EncompassContactType;
  correspondence_contact_role: EncompassContactType;

  customer_service_phone_override: string | null;
  servicer_name_override: string | null;
  correspondence_attention: string | null;
  qwr_attention: string | null;

  online_payments_enabled: boolean;
  /** Manual override; null uses the configured payment contact's synced website. */
  online_payments_url: string | null;
  pay_by_phone_enabled: boolean;
  pay_by_phone_number: string | null;
  auto_deduction_enabled: boolean;
  auto_deduction_instructions: string | null;

  insurance_impact_statement: string | null;
  pa_servicer_codes: string[];
  property_states: string[];

  created_at: string;
  updated_at: string;
}

export interface GoodbyeLetterSettings {
  id: string;
  organizationId: string;
  templateId: string;

  paymentContactRole: EncompassContactType;
  customerServiceContactRole: EncompassContactType;
  correspondenceContactRole: EncompassContactType;

  customerServicePhoneOverride: string | null;
  servicerNameOverride: string | null;
  correspondenceAttention: string | null;
  qwrAttention: string | null;

  onlinePaymentsEnabled: boolean;
  /** Manual override; null uses the configured payment contact's synced website. */
  onlinePaymentsUrl: string | null;
  payByPhoneEnabled: boolean;
  payByPhoneNumber: string | null;
  autoDeductionEnabled: boolean;
  autoDeductionInstructions: string | null;

  insuranceImpactStatement: string | null;
  paServicerCodes: string[];
  propertyStates: string[];

  createdAt: string;
  updatedAt: string;
}

/**
 * Mutable subset accepted by upsertSettings. Caller never passes id,
 * organization_id, template_id, or timestamps.
 */
export type GoodbyeLetterSettingsInput = {
  paymentContactRole?: EncompassContactType;
  customerServiceContactRole?: EncompassContactType;
  correspondenceContactRole?: EncompassContactType;

  customerServicePhoneOverride?: string | null;
  servicerNameOverride?: string | null;
  correspondenceAttention?: string | null;
  qwrAttention?: string | null;

  onlinePaymentsEnabled?: boolean;
  onlinePaymentsUrl?: string | null;
  payByPhoneEnabled?: boolean;
  payByPhoneNumber?: string | null;
  autoDeductionEnabled?: boolean;
  autoDeductionInstructions?: string | null;

  insuranceImpactStatement?: string | null;
  paServicerCodes?: string[];
  propertyStates?: string[];
};

// ---------------------------------------------------------------------------
// Resolved payload for the letter
// ---------------------------------------------------------------------------

export interface GoodbyeLetterPostalAddress {
  /** Display name (servicer/lender) printed above the address lines. */
  name: string;
  street1: string;
  street2?: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface GoodbyeLetterData {
  /** Servicing loan that this resolved payload was generated from. */
  servicingLoanId: string;

  /** Date printed on the letter; resolved by the service, defaulting to `banker_recons.investor_purchase_date`. */
  noticeDate: string;

  // Borrower
  borrowerName: string;
  coBorrowerName?: string | null;
  borrowerMailingAddress: string[];
  propertyAddress: string[];

  // Loan identifiers
  lendwiseLoanNumber: string;
  investorLoanNumber: string;

  // Investor / lender / servicer naming
  investorName: string;
  newServicerName: string;
  sellerLegalName: string;

  // Dates
  purchaseDate: string;
  servicingTransferDate: string;
  lastPaymentToLendwise: string;

  /**
   * Payments whose due_date fell within the Lendwise responsibility window
   * (first_payment_date <= due_date < paymentCutoffDate). Sorted ascending.
   */
  lendwisePaymentDueDates: string[];
  servicerPaymentsStartDate?: string;
  lendwisePayments?: Array<{ dueDate: string; amount: number | null; status: 'due' | 'received' }>;
  nextPayment?: { dueDate: string; amount: number | null; payTo: 'lendwise' | 'servicer' } | null;
  correction?: { originalAuditId: string; originalNotificationFactId: string; originalNoticeDate: string };

  // Mailing blocks
  paymentMailingAddress: GoodbyeLetterPostalAddress;
  /** All Other Correspondence; absent only in historical resolved payloads. */
  customerServiceMailingAddress?: GoodbyeLetterPostalAddress;
  /** Notices of Error / Requests for Information. */
  correspondenceMailingAddress: GoodbyeLetterPostalAddress;

  // Transferor block (Lendwise)
  transferor: {
    name: string;
    address: GoodbyeLetterPostalAddress;
    phone: string;
  };
  newServicerPhone: string;
  /** Payment contact phone for the New Servicer box; absent only in historical payloads. */
  paymentContactPhone?: string;

  // Attention lines
  correspondenceAttention: string;
  qwrAttention: string;
  paymentContactAttention?: string | null;
  customerServiceAttention?: string | null;
  correspondenceContactName?: string | null;

  // Payment Options — values resolved for print/PDF rendering.
  paymentOptions: {
    online: { enabled: boolean; url: string | null };
    payByPhone: { enabled: boolean; number: string | null };
    autoDeduction: { enabled: boolean; instructions: string | null };
  };

  /** Insurance impact paragraph text — already resolved to either the override or the default. */
  insuranceImpactStatement: string;

  /**
   * Always false: notice timing never blocks a letter, so there is no override
   * to record. Kept because the audit RPC takes it as p_short_notice_override.
   */
  shortNoticeOverrideUsed: boolean;

  // IDs included for the audit-trail write
  templateId: string;
  settingsId: string;
  templateAssignmentId?: string | null;
  servicerSelection?: { templateId: string; source: string | null; selectedAt: string | null };
  resolvedProduct?: string | null;
  templateResolverSource?: string | null;
  templateResolverConfidence?: string | null;
  auditSources?: {
    template?: Record<string, unknown> | null;
    assignment?: Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
    contacts?: Record<string, unknown>[];
    payment?: import('./goodbye-letter-payment.ts').GoodbyePaymentEvidence & { lastSyncAt: string | null };
  };
}

// ---------------------------------------------------------------------------
// Service options + error taxonomy
// ---------------------------------------------------------------------------

export interface GoodbyeLetterOptions {
  noticeDate?: string;
  draft?: boolean;
}

export type GoodbyeLetterPrereqCode =
  | 'SERVICING_LOAN_NOT_FOUND'
  | 'ENCOMPASS_LOAN_NOT_FOUND'
  | 'BANKER_RECON_MISSING'
  | 'SERVICER_NOT_SELECTED'
  | 'NO_TEMPLATE'
  | 'TEMPLATE_AMBIGUOUS'
  | 'TEMPLATE_PRODUCT_UNRESOLVED'
  | 'TEMPLATE_PRODUCT_NOT_ASSIGNED'
  | 'TEMPLATE_PRODUCT_AMBIGUOUS'
  | 'TEMPLATE_PRODUCT_MISMATCH'
  | 'TEMPLATE_CHANGED_DURING_GENERATION'
  | 'TEMPLATE_SCOPE_MISSING'
  | 'TEMPLATE_NOT_SYNCED'
  | 'GOODBYE_SETTINGS_MISSING'
  | 'LOAN_DATA_INCOMPLETE'
  | 'PAYMENT_DATA_INCOMPLETE'
  | 'PAYMENT_DATA_CONFLICT'
  | 'PAYMENT_PERIOD_UNSUPPORTED'
  | 'TEMPLATE_DATA_INCOMPLETE'
  | 'SETTINGS_INCOMPLETE'
  | 'TRANSFEROR_DATA_INCOMPLETE';

export class GoodbyeLetterPrereqError extends Error {
  readonly code: GoodbyeLetterPrereqCode;
  readonly missingFields?: string[];

  constructor(code: GoodbyeLetterPrereqCode, message: string, missingFields?: string[]) {
    super(message);
    this.name = 'GoodbyeLetterPrereqError';
    this.code = code;
    this.missingFields = missingFields;
  }
}
